import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react"
import type {
  SuggestionGenerator,
  SuggestionKind,
  InteractiveTarget,
  LayerId,
} from "./types"
import { PICK_CURSORS } from "./pick_ui"

interface SuggestionContextValue {
  register: (target: InteractiveTarget) => void
  unregister: (id: string) => void
  updateElement: (id: string, el: HTMLElement | null) => void
  getTarget: (id: string) => InteractiveTarget | undefined
  listTargets: () => InteractiveTarget[]
  /** Run a target's WRITE generator against its live context: its own `generate` if it has one, else the
   *  shared generator for its `kind`. Rejects if neither is available. */
  generate: (target: InteractiveTarget) => Promise<string>
  /** The interaction layer that's currently armed (null = none). "write" is the pick tool. */
  activeLayer: LayerId | null
  setActiveLayer: (layer: LayerId | null) => void
  /** Back-compat view of the "write" layer: pickMode === (activeLayer === "write"). */
  pickMode: boolean
  setPickMode: (on: boolean) => void
  /** Reactive count of registered targets per groupId — drives the section "pulsing dot". */
  groupCounts: Record<string, number>
  /** Reactive total of registered targets. */
  targetCount: number
  /** URL of the cursor image shown while pick mode is armed (default: the inline violet arrow). */
  cursorUrl: string
}

const SuggestionContext = createContext<SuggestionContextValue | null>(null)

export const useSuggestionContext = (): SuggestionContextValue => {
  const ctx = useContext(SuggestionContext)
  if (!ctx)
    throw new Error("useSuggestionTarget / usePickMode must be used within a <SuggestionTargetProvider>")
  return ctx
}

/** Non-throwing variant — for the Grug menu to gate the workflow when no provider is mounted. */
export const useMaybeSuggestionContext = (): SuggestionContextValue | null =>
  useContext(SuggestionContext)

export interface SuggestionTargetProviderProps {
  /** Shared per-kind generators for the write layer. Only needed for fields that name a `kind` instead of
   *  bringing their own `generate`. A named kind with no generator rejects when the user tries to generate. */
  generators: Partial<Record<SuggestionKind, SuggestionGenerator>>
  /** Cursor image URL for pick mode. Defaults to the inline violet arrow (PICK_CURSORS.arrow); pass
   *  PICK_CURSORS.hand or any URL to override. */
  cursorUrl?: string
  children: React.ReactNode
}

/**
 * Holds the registry of interactive targets + the active-layer flag. The registry is a ref (not state) so a
 * field mounting/unmounting doesn't re-render every consumer — an overlay reads it live when its layer arms.
 */
export const SuggestionTargetProvider: React.FC<SuggestionTargetProviderProps> = ({
  generators,
  cursorUrl = PICK_CURSORS.arrow,
  children,
}) => {
  const registry = useRef(new Map<string, InteractiveTarget>())
  const [activeLayer, setActiveLayer] = useState<LayerId | null>(null)
  // Per-group registered-target counts. Kept as state (not just the ref) so a section's pulsing dot reacts
  // when its fields mount/unmount.
  const [groupCounts, setGroupCounts] = useState<Record<string, number>>({})
  const [targetCount, setTargetCount] = useState(0)

  const bumpGroup = useCallback((groupId: string, delta: number) => {
    setGroupCounts((counts) => {
      const next = { ...counts }
      const value = (next[groupId] ?? 0) + delta
      if (value <= 0) delete next[groupId]
      else next[groupId] = value
      return next
    })
  }, [])

  const register = useCallback(
    (target: InteractiveTarget) => {
      registry.current.set(target.id, target)
      setTargetCount((n) => n + 1)
      if (target.groupId) bumpGroup(target.groupId, 1)
    },
    [bumpGroup]
  )
  const unregister = useCallback(
    (id: string) => {
      const target = registry.current.get(id)
      registry.current.delete(id)
      setTargetCount((n) => Math.max(0, n - 1))
      if (target?.groupId) bumpGroup(target.groupId, -1)
    },
    [bumpGroup]
  )
  const updateElement = useCallback((id: string, el: HTMLElement | null) => {
    const target = registry.current.get(id)
    if (target) target.element = el
  }, [])
  const getTarget = useCallback((id: string) => registry.current.get(id), [])
  const listTargets = useCallback(() => Array.from(registry.current.values()), [])

  const generate = useCallback(
    (target: InteractiveTarget) => {
      const cap = target.capabilities.write
      if (!cap)
        return Promise.reject(new Error(`Target "${target.id}" has no write capability`))
      const context = cap.getContext()
      // A field-defined generator is the field's own API and wins; otherwise fall back to the shared kind map.
      if (cap.generate) return cap.generate(context)
      const shared = cap.kind ? generators[cap.kind] : undefined
      if (!shared)
        return Promise.reject(
          new Error(
            cap.kind
              ? `No generator registered for suggestion kind "${cap.kind}"`
              : `Target "${target.id}" defines no generator`
          )
        )
      return shared(target, context)
    },
    [generators]
  )

  const pickMode = activeLayer === "write"
  const setPickMode = useCallback(
    (on: boolean) => setActiveLayer(on ? "write" : null),
    []
  )

  const value = useMemo<SuggestionContextValue>(
    () => ({
      register,
      unregister,
      updateElement,
      getTarget,
      listTargets,
      generate,
      activeLayer,
      setActiveLayer,
      pickMode,
      setPickMode,
      groupCounts,
      targetCount,
      cursorUrl,
    }),
    [
      register,
      unregister,
      updateElement,
      getTarget,
      listTargets,
      generate,
      activeLayer,
      setActiveLayer,
      pickMode,
      setPickMode,
      groupCounts,
      targetCount,
      cursorUrl,
    ]
  )

  return <SuggestionContext.Provider value={value}>{children}</SuggestionContext.Provider>
}

/**
 * Arm/disarm an interaction layer by id. The general primitive behind usePickMode: a future "Explain this
 * page" or a product tour calls useLayer("explain") / useLayer("tour") to drive its own overlay.
 */
export const useLayer = (layerId: LayerId) => {
  const ctx = useSuggestionContext()
  return {
    active: ctx.activeLayer === layerId,
    activate: () => ctx.setActiveLayer(layerId),
    deactivate: () => ctx.setActiveLayer(null),
  }
}

/** For the Grug "Help me write…" workflow: toggle the write pick tool on/off (the "write" layer). */
export const usePickMode = () => {
  const ctx = useSuggestionContext()
  return {
    active: ctx.pickMode,
    activate: () => ctx.setPickMode(true),
    deactivate: () => ctx.setPickMode(false),
  }
}

/**
 * Non-throwing read of whether the pick tool is currently armed. For UI that wants to react to pick mode but
 * renders fine without a provider (e.g. a section nav button that shows a "pickable field inside" dot, and
 * also appears in Storybook/tests where no SuggestionTargetProvider is mounted). Returns false when absent.
 */
export const useMaybePickMode = (): boolean =>
  useContext(SuggestionContext)?.pickMode ?? false

/**
 * For a section's nav button: whether the pick tool is active and this group holds at least one pickable
 * field. Render a pulsing <SuggestionDot> when both are true.
 */
export const useSuggestionGroup = (groupId: string) => {
  const ctx = useSuggestionContext()
  return {
    pickMode: ctx.pickMode,
    hasTargets: (ctx.groupCounts[groupId] ?? 0) > 0,
  }
}
